import { FormsEntities, FormsPayloadEntities, Mobile } from '@bimser/common';
import * as LayoutCreatorEntities from '@bimser/components/dist/BCLayoutCreator/common/entities';
import {
	FormItemTypes,
	IDictionary,
	difference,
	extend,
	getEnumTypeKeys,
	isNullOrUndefined,
	isPrimitive,
	mobile
} from '@bimser/core';
import { Map } from 'immutable';
import type { PropertyPath } from 'lodash';
import _cloneDeep from 'lodash/cloneDeep';
import _clone from 'lodash/clone';
import _get from 'lodash/get';
import _isArray from 'lodash/isArray';
import _isEqual from 'lodash/isEqual';
import _mergeWith from 'lodash/mergeWith';
import _set from 'lodash/set';
import _update from 'lodash/update';
import Hash from 'object-hash';
import { JSX } from 'react';
import { FormControlEntities } from '../../..';
import { IDataSource, IEventItem, IOnLoadArgs, IPropertyField } from '../../../entities/common';
import { IUserEvent } from '../../../entities/services';
import { getActionValue } from '../../../sagas/ruleValidation/action';
import EventEmitter from '../../Emitter';
import Form from '../components/Form';
import {
	IDataLoadEventArgs,
	IPropertyChangedEventArgs,
	IPropertyChangingEventArgs
} from '../entities/EventArgs';
import { IControlProperties } from '../entities/IControlProperties';
import { IDataSourceParameterFieldPropChange } from '../entities/IDataSourceParameterFieldPropChange';
import { proxyCreator } from '../helper';
import { getValueControl } from '../helper/getValueControl';
export default class Control {
	private __eventEmitter: EventEmitter;
	protected __isInitializing: boolean;
	__proxyChanging: this;
	__proxyChanged: this;
	protected __excludeChangingProperties: Array<string>;
	protected __excludeChangedProperties: Array<string>;
	protected __propertyMapping: IDictionary<string>;
	protected __triggerDataSourceList: IDictionary<string>;
	__isInitialized: boolean = false;
	name: string;
	controlId: string;
	controlType: FormItemTypes.AllTypes;
	serverEvents: IEventItem[];
	clientEvents: IEventItem[];

	__form: Form;

	constructor() {
		this.__eventEmitter = new EventEmitter();
		this.__triggerPropertyChanging = this.__triggerPropertyChanging.bind(this);
		this.__triggerPropertyChanged = this.__triggerPropertyChanged.bind(this);

		this.__onControlClick = this.__onControlClick.bind(this);
		this.__onControlDoubleClick = this.__onControlDoubleClick.bind(this);
		this.__onChangeMLTextValue = this.__onChangeMLTextValue.bind(this);
		this.__setControlProperties = this.__setControlProperties.bind(this);
		this.__excludeChangingProperties = [];
		this.__propertyMapping = {};
		this.__triggerDataSourceList = {};
		this.serverEvents = [];
		this.clientEvents = [];
		this.__proxyChanging = proxyCreator(this, this.__triggerPropertyChanging);
		this.__proxyChanged = proxyCreator(this, this.__triggerPropertyChanged);
		this.subscribe('event:OnLoad', this.__onLoad.bind(this));
		this.subscribe('trigger:RefreshControl', (value: string) => {
			Mobile.MobileView.setControlProperties(
				this.name,
				this.__getComponentData(),
				this.__form?.__windowId || ''
			);
		});
	}

	initialize() {
		// base class
	}

	__onLoad(args?: IOnLoadArgs) {
		// base class
	}

	__controlsUpdated(controls: FormsPayloadEntities.IPropertyChangeEventArgs[]) {
		// base class
	}

	__onFormScrollStart() {
		// base class
	}

	__onFormResize() {
		// base class
	}

	__onBeforePrint(): boolean {
		return false;
	}

	__onAfterPrint() {
		// base class
	}

	__getMLClassPath(): string[] {
		// base class
		return [];
	}

	get<T extends this, K extends keyof T>(key: K): T[K] {
		return (this as any)[key];
	}

	set<T extends this, K extends keyof T>(key: K, value: T[K]): Promise<void> & Pick<T, 'set'> {
		const promise = new Promise<void>(resolve => {
			if (key && typeof key === 'string') {
				const transactionId = this.__form.beginTransaction();
				(this.__proxyChanging as any)[key] = value;
				this.__form.endTransaction(transactionId).then(() => {
					resolve();
				});
			}
		});

		const setNext = <T extends this, K extends keyof T>(
			key: K,
			value: T[K]
		): Promise<[void, void]> & Pick<T, 'set'> => {
			const nextPromise: Promise<void> = this.set(key, value);
			const allPromise = Promise.all([promise, nextPromise]);

			(allPromise as any).set = setNext;
			return allPromise as any;
		};

		(promise as any).set = setNext;
		return promise as any;
	}

	getIn(keyPath: PropertyPath) {
		return _get(this, keyPath);
	}

	setIn<T extends this>(keyPath: PropertyPath, value: any): Promise<void> & Pick<T, 'setIn'> {
		const promise = new Promise<void>(resolve => {
			if (keyPath) {
				this.__form.beginTransaction();
				_set(this.__proxyChanging, keyPath, value);
				this.__form.endTransaction().then(() => {
					resolve();
				});
			}
		});

		const setInNext = (
			keyPath: PropertyPath,
			value: any
		): Promise<[void, void]> & Pick<T, 'setIn'> => {
			const nextPromise: Promise<void> = this.setIn(keyPath, value);
			const allPromise = Promise.all([promise, nextPromise]);

			(allPromise as any).setIn = setInNext;
			return allPromise as any;
		};

		(promise as any).setIn = setInNext;
		return promise as any;
	}

	__dataSourceLoadMap: IDictionary<any> = {};
	__fieldParams: IDictionary<any> = {};
	__reloadDataSourceFlag: boolean = false;
	__dataSourceValueEntryFlag: boolean = false;

	__reloadDataSource(
		dataSource: IDataSource,
		forceRefresh?: boolean,
		firstReload?: boolean,
		bypass?: boolean
	) {
		return new Promise<void>((resolve, reject) => {
			if (
				(forceRefresh || dataSource?.parameters?.length) &&
				this.__form.displayMode === 'execution'
			) {
				const params = dataSource.parameters;
				const allParamValues: IDictionary<any> = {};
				let requiredFieldError: boolean = false;

				const allowReload = this.__allowReload(
					allParamValues,
					dataSource,
					forceRefresh,
					bypass
				);

				const subscribed = params.some(param => {
					if (param.valueModifier === 'fieldSelection' && !requiredFieldError) {
						requiredFieldError = this.__subscribeForReloadDataField(
							param,
							allParamValues,
							forceRefresh
						);

						if (allowReload && firstReload && requiredFieldError) {
							const controlId = param.field.split('.').shift();
							const control: Control = this.__form[controlId];

							if (!param.field.includes('selectedItem.field.') || !control) {
								return;
							}

							const fn = (_args: IDataLoadEventArgs) => {
								control.off('event:OnDataLoad', fn);

								this.__reloadDataSource(dataSource, true)
									.then(resolve)
									.catch(reject);
							};

							control.subscribe('event:OnDataLoad', fn);

							return true;
						}
					}
				});

				if (subscribed) {
					return;
				}

				if (allowReload && !requiredFieldError) {
					this.__dataSourceLoadMap[dataSource.name] = Hash(allParamValues);
					this.__reloadDataSourceFlag = true;

					this.__onLoad({
						externalData: { fieldParams: allParamValues },
						reload: true,
						forceRefresh
					});
					resolve();
				} else {
					reject();
				}
			} else {
				reject();
			}
		});
	}

	private __allowReload(
		allParamValues: IDictionary<any>,
		dataSource: IDataSource,
		forceRefresh: boolean,
		bypass?: boolean
	) {
		const subsribedAlready = Object.keys(this.__triggerDataSourceList).length;

		const requiredValidValueCount = Object.keys(allParamValues).length;
		const validValueCount = Object.keys(allParamValues)
			.map(key => (isNullOrUndefined(allParamValues[key]) ? false : true))
			.filter(result => result).length;

		const isLoadHashNotEquals =
			this.__dataSourceLoadMap[dataSource.name] != Hash(allParamValues);
		const shouldReload = !this.__reloadDataSourceFlag || dataSource?.parameters?.length;
		const isParametersValid = validValueCount === requiredValidValueCount;

		let allowReload: boolean =
			((shouldReload || isLoadHashNotEquals) && !subsribedAlready && isParametersValid) ||
			forceRefresh ||
			bypass;
		const allParamsValueCount = Object.keys(allParamValues).length;
		if (!allParamsValueCount && !this.__dataSourceValueEntryFlag) {
			this.__dataSourceValueEntryFlag = true;
		} else if (this.__dataSourceValueEntryFlag) {
			allowReload = false;
		}

		return allowReload;
	}

	private __subscribeForReloadDataField(
		param: FormControlEntities.Common.IDataSourceParameter,
		allParamValues: IDictionary<any>,
		forceRefresh: boolean
	) {
		let fieldArr = param.field.split('.'),
			controlId = fieldArr.shift(),
			propertyPath = fieldArr.join('.'),
			isRequired: boolean = false;

		const fieldKeys = param.field.replace(controlId + '.', '');
		const value = getActionValue(controlId, fieldKeys, _get(this.__form, 'items', null));

		if (param.isRequired && isNullOrUndefined(value)) {
			isRequired = true;
		} else {
			allParamValues[param.field] = getValueControl(this.__form, controlId, value);
		}

		if (!this.__triggerDataSourceList[param.field]) {
			this.__triggerDataSourceList[param.field] = 'Subscribed';
			const control: Control = this.__form[controlId];

			if (control) {
				control.subscribe(
					'event:OnPropertyChanged',
					(args: IPropertyChangedEventArgs<any>) => {
						this.__form.applyChanges().then(() => {
							this.__controlPropertyChangedFn({
								args,
								propertyPath,
								param,
								controlId,
								forceRefresh
							});
						});
						if (
							(this as any).dataSource?.runAtServer &&
							param.field == `${controlId}.${args.name}`
						) {
							const argument = {
								name: args.name,
								newValue: args?.newValue,
								oldValue: args?.oldValue
							};

							this.__form
								.__runServerEvent(
									{
										argument,
										controlId: args.controlName,
										name: 'PropertyChanged',
										sender: 'web'
									},
									undefined
								)
								.then(arg => {
									this.__form.__executedServerEvent = arg.executedServerEvent;

									if (arg.loadActionData.isDMFileSecretModified) {
										this.__form.__loadDocumentViewer();
									}

									if (arg.loadActionData.isRDFileSecretModified) {
										this.__form.isLoadingRDObjects = false;
										this.__form.__getRDObjects();
									}
								});
						}
					}
				);
			}
		}

		return isRequired;
	}

	private __controlPropertyChangedFn(data: IDataSourceParameterFieldPropChange) {
		const { args, propertyPath, param, controlId, forceRefresh } = data;
		if (propertyPath === args.name) {
			const dataSource: IDataSource = (this as any).dataSource;
			let requiredError = false;

			dataSource?.parameters?.forEach(p => {
				if (p.valueModifier !== 'fieldSelection') {
					return;
				}
				if (p.name === param.name) {
					this.__fieldParams[param.field] = getValueControl(
						this.__form,
						controlId,
						args.newValue
					);
				} else {
					const paramFieldSplit = p.field.split('.');
					const cId = paramFieldSplit[0];
					paramFieldSplit.shift();

					const val = getActionValue(
						cId,
						paramFieldSplit.join('.'),
						_get(this.__form, 'items', null)
					);

					this.__fieldParams[p.field] = getValueControl(this.__form, controlId, val);
				}

				if (!requiredError) {
					requiredError =
						p.isRequired &&
						(isNullOrUndefined(this.__fieldParams[p.field]) ||
							this.__fieldParams[p.field] === '');
				}
			});

			this.__onLoad({
				externalData: { fieldParams: this.__fieldParams },
				reload: true,
				forceRefresh,
				parameterChanged: true,
				requiredError
			});
		}
	}

	__pendingServerEvent: { requests: IDictionary<IUserEvent<any>>; changeTimeout: number } = {
		requests: {},
		changeTimeout: null
	};

	__tasks: Array<{ fn: () => Promise<any>; resolver: (response: any) => void }> = [];
	__runEventPending: boolean = false;
	__createQueue(task: () => Promise<any>) {
		const run = () => {
			const t = this.__tasks.shift();

			if (t) {
				t.fn().then((response: any) => {
					t.resolver(response);

					const callbackFn = (data: any) => {
						if (response.guid == data.guid) {
							this.off('system:FormItemsLoaded', callbackFn);
							run();
						}
					};

					this.subscribe('system:FormItemsLoaded', callbackFn);
				});
			} else {
				this.__runEventPending = false;
			}
		};

		return new Promise(done => {
			this.__tasks.push({ fn: task, resolver: done });
			if (!this.__runEventPending) {
				this.__runEventPending = true;
				run();
			}
		});
	}

	private __triggerPropertyChanged(value: any, path: string[]) {
		const mappingProperty = this.__propertyMapping && this.__propertyMapping[path.join('.')];
		if (mappingProperty) {
			return false;
		}

		const localPathMap = path.join('.');

		if (value !== undefined) {
			let hasDifference = false;
			let previousValue: any = null;
			let globalObjectDiff = {};
			_update(this, localPathMap, (prevValue: any) => {
				const isValuesNotNull = value !== null && prevValue !== null;
				const isValuesArray = [value, prevValue].filter(Array.isArray).length;
				const isValuesObject = [value, prevValue].filter(v => typeof v === 'object').length;

				if (isValuesNotNull) {
					if (
						(isValuesArray === 1 ||
							(isValuesArray === 2 && value.length !== prevValue.length)) &&
						!_isEqual(value, prevValue)
					) {
						hasDifference = true;
					} else if (isValuesObject === 2) {
						const currentValue = extend(true, {}, prevValue);
						const newValue = extend(true, {}, value);
						globalObjectDiff = difference(newValue, currentValue);
						if (globalObjectDiff && Object.keys(globalObjectDiff).length) {
							hasDifference = true;
						}
					} else {
						hasDifference = value !== prevValue;
					}
				} else {
					hasDifference = value !== prevValue;
				}

				let returnValue = prevValue;

				if (hasDifference) {
					if (_isArray(value)) {
						returnValue = value;
					} else if (
						typeof value === 'object' &&
						value !== null &&
						!value['@@__IMMUTABLE_RECORD__@@']
					) {
						returnValue = _mergeWith(
							_cloneDeep(prevValue),
							value,
							(objValue: any, srcValue: any) => {
								if (_isArray(objValue)) {
									return srcValue;
								}
							}
						);
					} else {
						returnValue = value;
					}
				}
				previousValue = prevValue;
				return returnValue;
			});

			if (hasDifference && !this.__isInitializing) {
				let propertyChangedArgs: IPropertyChangedEventArgs<any> = {
					name: localPathMap,
					newValue: _get(this, localPathMap),
					oldValue: previousValue,
					controlName: this.name
				};

				const isPropertyExcluded = this.__excludeChangedProperties?.find(i => {
					const rootKey = localPathMap.split('.')[0];
					return i === rootKey;
				})
					? true
					: false;

				if (!isPropertyExcluded) {
					if (propertyChangedArgs.newValue !== propertyChangedArgs.oldValue) {
						this.emit('event:OnPropertyChanged', propertyChangedArgs); // Trigger for root object

						const triggerOnPropertyChangedForEveryKey = (obj: IDictionary<any>) => {
							Object.keys(obj).forEach(key => {
								const subPath = `${localPathMap}.${key}`;
								const newValue = _get(this, subPath);
								const oldValue = _get(previousValue, key);

								propertyChangedArgs = {
									name: subPath,
									newValue,
									oldValue,
									controlName: this.name
								};

								if (
									isPrimitive(newValue) &&
									isPrimitive(oldValue) &&
									newValue !== oldValue
								) {
									this.emit('event:OnPropertyChanged', propertyChangedArgs); // Trigger for sub keys
								}

								if (!isNullOrUndefined(newValue) && typeof newValue === 'object') {
									triggerOnPropertyChangedForEveryKey(newValue);
								}
							});
						};

						triggerOnPropertyChangedForEveryKey(globalObjectDiff);
					}
				}
			}
		}
	}

	__resolvePropertyChangeCount(scope: FormsEntities.IPropertyChangeTransaction) {
		this.__form.decreasePendingPropertyCount(scope.transactionId);
		if (this.__form.__transactions[scope.transactionId]?.pendingPropertyChangeCount === 0) {
			this.__form.setTransactionChangingStatus(scope.transactionId, false);
		}
		this.__form.emit('system:PropertyChangePerformed', scope);
	}

	__getWindowId() {
		if (this.__form.__formViewerControlType) {
			return `${this.__form.__formViewerFormId}_${this.__form.__formViewerControlType}_${this.__form.__formViewerControlName}_${this.__form.__formViewerViewName}_${this.__form.documentId}`;
		}
		return this.__form.__windowId;
	}

	__checkIncreaseCommitId(path: string[]): boolean {
		// base class
		return false;
	}

	__triggerPropertyChanging(value: any, path: string[]) {
		const isExcluded = this.__excludeChangingProperties.indexOf(path.join('.')) > -1;
		const localPathMap = path.join('.');

		if (this.__propertyMapping) {
			if (this.__propertyMapping[localPathMap]) {
				path = this.__propertyMapping[localPathMap].split('.');
			}

			if (this.__propertyMapping[path[0]]) {
				path[0] = this.__propertyMapping[path[0]];
			}
		}

		const MLClassPath = this.__getMLClassPath();
		const foundMLPath = MLClassPath.find(i => i.startsWith(localPathMap));

		value = _clone(value);

		if (foundMLPath) {
			const MLPropertyPath = foundMLPath.split('.');
			let targetPath = MLPropertyPath.splice(1, MLPropertyPath.length).join('.');

			if (targetPath) {
				if (targetPath.startsWith('__array__')) {
					value = value.map(i => {
						targetPath = targetPath.replace('__array__.', '');
						const mlNewValue = _get(i, targetPath);
						if (mlNewValue?.getData) {
							_set(i, targetPath, mlNewValue.getData());
						}
						return i;
					});
				} else {
					const mlNewValue = _get(value, targetPath);
					if (mlNewValue?.getData) {
						_set(value, targetPath, mlNewValue.getData());
					}
				}
			} else if (value?.getData) {
				value = value.getData();
			}
		}

		if (isExcluded) {
			_update(this, localPathMap, () => value);
			return;
		}

		let tempTransactionId = this.__form.__transactionId;

		if (!tempTransactionId) {
			tempTransactionId = this.__form.beginTransaction({
				timeout: 300,
				autoDispatchTimeout: 10
			});
		}

		this.__form.increasePendingPropertyCount(tempTransactionId);
		this.__form.setTransactionChangingStatus(tempTransactionId, true);

		const scope: FormsEntities.IPropertyChangeTransaction = {
			transactionId: tempTransactionId,
			data: [
				{
					dataField: path,
					value: value,
					handled: true,
					id: this.name,
					doNotIncreaseCommitId: this.__checkIncreaseCommitId(path)
				}
			]
		};

		if (this.__form.__formViewerControlType) {
			scope['windowId'] = this.__getWindowId();
		}

		const propertyPath = path.join('.');

		let oldValue = _get(this, propertyPath);

		if (typeof oldValue === 'object' && !Array.isArray(oldValue)) {
			oldValue = extend(true, {}, oldValue);
		}

		this.emit('system:PropertyChanging:' + path[0], scope.data[0])
			.then(() => {
				const args: IPropertyChangingEventArgs<any> = {
					name: propertyPath,
					newValue: value,
					oldValue,
					cancel: false
				};

				this.emit('event:OnPropertyChanging', args)
					.then(() => {
						if (!args.cancel) {
							done();
						} else {
							this.__resolvePropertyChangeCount(scope);
						}
					})
					.catch(() => {
						this.__resolvePropertyChangeCount(scope);
					});
			})
			.catch(() => {
				this.__resolvePropertyChangeCount(scope);
			});

		const done = () => {
			this.emit('system:PropertyChanged', scope);
			this.__resolvePropertyChangeCount(scope);
		};

		if (scope.data[0].handled) {
			if (scope.data[0].handled instanceof Promise) {
				scope.data[0].handled.then(() => {
					done();
				});
			}
		} else {
			done();
		}
	}

	__loadProperties(data: IControlProperties) {
		this.__proxyChanged.name = data.name;
		this.__proxyChanged.controlId = data.controlId;
		this.__proxyChanged.controlType = data.controlType;
		this.__proxyChanged.serverEvents = data.serverEvents;
		this.__proxyChanged.clientEvents = data.clientEvents;
	}

	__loadChildren(data: LayoutCreatorEntities.NormalizedLayoutStructureItems) {
		// Base function
	}

	hasSubscriber(eventName: string) {
		return this.__eventEmitter.hasSubscriber(eventName);
	}

	emit<TData>(eventName: string, data: TData) {
		return this.__eventEmitter.emit(eventName, data);
	}

	off(eventName?: string, event?: Function) {
		this.__eventEmitter.off(eventName, event);
	}

	subscribe(eventName: string, func: Function) {
		return this.__eventEmitter.subscribe(eventName, func);
	}

	__beginInitialize() {
		this.__isInitializing = true;
		this.__isInitialized = false;
	}

	__endInitialize() {
		this.__isInitializing = false;
		this.__isInitialized = true;
	}

	/** * text -> set edilecek değer
	 *  * Path -> path değeri objeler arasında nokta (".") koyarak gönderilmelidir. Example: style.caption
	 *  * Result path -> this.__proxyChanging.style.caption
	 *  * Purpose of usage -> MultiLanguageText tipindeki setText vb. ile yapılan değer değişikliklerin state' e yazılması.
	 */
	__onChangeMLTextValue(text: string, path: string, culture: string, existCultures: string[]) {
		const _path: string[] = path.split('.');
		const property = { ..._get(this, _path) };

		Object.keys(property).forEach(key => {
			if (!existCultures.includes(key)) {
				delete property[key];
			}
		});
		property[culture] = text;

		_set(this, ['__proxyChanging', ..._path], property);
	}

	__onControlClick(e?: React.MouseEvent<HTMLDivElement, MouseEvent>) {
		const argument = {
			altKey: e.altKey,
			button: e.button,
			buttons: e.buttons,
			clientX: e.clientX,
			clientY: e.clientY,
			ctrlKey: e.ctrlKey,
			metaKey: e.metaKey,
			movementX: e.movementX,
			movementY: e.movementY,
			pageX: e.pageX,
			pageY: e.pageY,
			screenX: e.screenX,
			screenY: e.screenY,
			shiftKey: e.shiftKey
		};

		const args = {
			argument,
			controlId: this.name,
			name: 'Click',
			sender: 'web'
		};

		this.emit('event:OnClick', argument);
		this.emit('event:server:OnClick', args);
	}

	__onControlDoubleClick(e?: React.MouseEvent<HTMLDivElement, MouseEvent>) {
		const argument = {
			altKey: e.altKey,
			button: e.button,
			buttons: e.buttons,
			clientX: e.clientX,
			clientY: e.clientY,
			ctrlKey: e.ctrlKey,
			metaKey: e.metaKey,
			movementX: e.movementX,
			movementY: e.movementY,
			pageX: e.pageX,
			pageY: e.pageY,
			screenX: e.screenX,
			screenY: e.screenY,
			shiftKey: e.shiftKey
		};

		const args = {
			argument,
			controlId: this.name,
			name: 'DoubleClick',
			sender: 'web'
		};

		this.emit('event:OnDoubleClick', argument);
		this.emit('event:server:OnDoubleClick', args);
	}

	__getPropertyFields(properties?: Map<string, any>): IPropertyField[] {
		return [
			{
				name: 'name',
				type: 'string',
				readOnly: true
			},
			{
				name: 'controlId',
				type: 'string',
				readOnly: true
			},
			{
				name: 'controlType',
				type: 'enum',
				enumMembers: getEnumTypeKeys(FormItemTypes.EControlTypes).map(
					type => type as keyof typeof FormItemTypes.EControlTypes
				) as string[],
				readOnly: true
			}
		];
	}

	__getComponentData(): IDictionary<any> {
		return {
			name: this.name,
			controlId: this.controlId,
			controlType: this.controlType,
			serverEvents: this.serverEvents,
			clientEvents: this.clientEvents
		};
	}

	__setControlProperties() {
		if (mobile().any()) {
			Mobile.MobileView.setControlProperties(
				this.name,
				this.__getComponentData(),
				this.__form?.__windowId || ''
			);
		}
	}

	__render(children?: React.ReactNode): JSX.Element {
		this.emit('event:OnRender', {});
		this.__setControlProperties();

		return <>{children}</>;
	}
}
